import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const CATEGORY_COLOR = {
  Tokenomics: '#f59e0b',
  DeFi:       '#10b981',
  Layer:      '#3b82f6',
  Market:     '#8b5cf6',
  Technical:  '#06b6d4',
  Governance: '#ec4899',
  Trading:    '#f97316',
  Security:   '#ef4444',
};

const POPUP_W = 340;
const POPUP_H = 280;
const OFFSET  = 14; // gap from cursor

function computePosition(mouseX, mouseY) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 10;

  let left = mouseX + OFFSET;
  let top  = mouseY + OFFSET;

  // Flip left if too close to right edge
  if (left + POPUP_W + margin > vw) left = mouseX - POPUP_W - OFFSET;
  // Flip up if too close to bottom edge
  if (top + POPUP_H + margin > vh) top = mouseY - POPUP_H - OFFSET;

  // Final clamp
  left = Math.max(margin, Math.min(left, vw - POPUP_W - margin));
  top  = Math.max(margin, Math.min(top,  vh - POPUP_H - margin));

  return { left, top };
}

export default function KnowledgePopup({ data, loading, error, mouseX, mouseY }) {
  const { t } = useTranslation();
  const pos = computePosition(mouseX ?? 0, mouseY ?? 0);
  const catColor = data ? (CATEGORY_COLOR[data.category] || '#64748b') : '#64748b';

  return (
    <div style={{ ...styles.popup, top: pos.top, left: pos.left }}>
      {loading && (
        <div style={styles.loadingBox}>
          <span style={styles.spinner} />
          <span style={{ color: '#4a6a8a', fontSize: 12 }}>{t('knowledgePopup.loading')}</span>
        </div>
      )}

      {error && (
        <div style={styles.errorBox}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span style={{ color: '#ef4444', fontSize: 13 }}>{error}</span>
        </div>
      )}

      {data && !loading && (
        <>
          <div style={styles.header}>
            <span style={styles.emoji}>{data.emoji || '📖'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.term}>{data.term}</div>
              {data.category && (
                <span style={{ ...styles.category, color: catColor, borderColor: catColor + '40', background: catColor + '15' }}>
                  {data.category}
                </span>
              )}
            </div>
          </div>

          <p style={styles.definition}>{data.definition}</p>

          {data.examples?.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Example</div>
              {data.examples.map((ex, i) => (
                <div key={i} style={styles.example}>"{ex}"</div>
              ))}
            </div>
          )}

          {data.related?.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Related</div>
              <div style={styles.relatedTags}>
                {data.related.map((r, i) => (
                  <span key={i} style={styles.relatedTag}>{r}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  popup: {
    position: 'fixed',
    zIndex: 9999,
    width: POPUP_W,
    background: '#ffffff',
    border: '1px solid #d0e2f5',
    borderRadius: 14,
    padding: '18px 20px 16px',
    boxShadow: '0 8px 32px rgba(31,88,164,0.15), 0 2px 8px rgba(31,88,164,0.08)',
    pointerEvents: 'none',
    animation: 'fadeIn 0.12s ease',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  emoji: {
    fontSize: 26,
    lineHeight: 1,
    flexShrink: 0,
  },
  term: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0d1f3c',
    marginBottom: 4,
  },
  category: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: 4,
    border: '1px solid',
    display: 'inline-block',
  },
  definition: {
    fontSize: 13,
    color: '#4a6a8a',
    lineHeight: 1.65,
    margin: '0 0 12px',
  },
  section: {
    marginBottom: 10,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: '#6b8dad',
    marginBottom: 5,
  },
  example: {
    fontSize: 12,
    color: '#4a6a8a',
    fontStyle: 'italic',
    lineHeight: 1.5,
    marginBottom: 3,
  },
  relatedTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
  },
  relatedTag: {
    fontSize: 11,
    color: '#4a6a8a',
    background: '#eef3fb',
    borderRadius: 5,
    padding: '2px 8px',
    border: '1px solid #d0e2f5',
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 0',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 0',
  },
  spinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid #d0e2f5',
    borderTopColor: '#1f58a4',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    flexShrink: 0,
  },
};
